'use client';

import { useEffect } from 'react';
import {
    buildSnowplowEvent,
    getSnowplowContext,
    getSnowplowProperties,
    initializeSnowplow,
    SNOWPLOW_CONTEXT_MAPPING
} from '@/tracking/snowplow/utils';
import type { EventType, SnowplowContextInput } from '@/tracking/snowplow/utils';
import { useStore } from '@/store/store';
import { useCoreEvents } from '@/hooks/use-core-events';
import { getCookieValue } from '@/utils/cookies/client';
import { COOKIES } from '@/utils/cookies';
import { useGlobalContext } from '@/store/contexts/global-context';

type SendEventToSnowplowProps = {
    event: EventType;
    eventProps: Record<string, unknown>;
    contexts?: SnowplowContextInput[];
};

export const useSnowplowUm = () => {
    const { logEvent } = useCoreEvents();
    const userPlayableBalance = useStore((state) => state.userContext.balance);
    const { isLoggedIn, isAndroidWrapper, isIosWrapper, isXsellWrapper, isAndroid, isIos, isMobile, region } =
        useGlobalContext();
    const userId = getCookieValue(COOKIES.USER_ID) as string;

    useEffect(() => {
        initializeSnowplow();
    }, []);

    useEffect(() => {
        if (!userId) {
            return;
        }

        initializeSnowplow()
            .then((snowplow) => {
                snowplow.setUserId(userId);
            })
            .catch((error) => {
                console.error('Error setting user ID in Snowplow UM:', error);
            });
    }, [userId]);

    const trackPageView = async (eventGlobalProperties: Record<string, unknown>) => {
        try {
            const snowplow = await initializeSnowplow();

            snowplow.trackPageView([
                getSnowplowContext(SNOWPLOW_CONTEXT_MAPPING.GLOBAL_PROPERTIES, eventGlobalProperties)
            ]);
        } catch (error) {
            console.error('[SnowplowModule.trackPageView] Error tracking page view', error);
        }
    };

    const sendEventToSnowplow = (props: SendEventToSnowplowProps) => {
        const { event: eventName, eventProps, contexts } = props;

        const eventGlobalProperties = getSnowplowProperties({
            balance: userPlayableBalance,
            isLoggedIn,
            region,
            isMobile,
            isAndroidWrapper: (isXsellWrapper && isAndroid) || isAndroidWrapper,
            isIosWrapper: (isXsellWrapper && isIos) || isIosWrapper
        });

        try {
            const builtEvent = buildSnowplowEvent(eventName, eventProps, contexts ?? [], eventGlobalProperties);
            logEvent({
                name: eventName,
                data: JSON.stringify(builtEvent),
                vendorTag: 'SNOWPLOW'
            });
        } catch (e) {
            console.error('[CoreEvents.logEvent] Error logging Snowplow event', e);
        }
    };

    return {
        sendEventToSnowplow,
        trackPageView
    };
};
